import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * phase-compiler-screen-batch item 8 — observe an element's own width.
 *
 * The PDF/A compliance panel has to lay out against the width the INSPECTOR
 * actually has, which item 1 turned into a variable (collapse the centre pane
 * and the Inspector may be most of the screen; leave it open and it may be
 * 320px). A CSS media query is the wrong tool — it asks about the VIEWPORT,
 * which here is unrelated to the panel's width. Container queries would fit,
 * but observing the element keeps the breakpoint in the same file as the
 * layout decision it drives, and is testable without a real layout engine.
 *
 * ## Why a CALLBACK ref rather than `useRef` + a mount effect
 *
 * The obvious version — `useRef` plus an effect with `[]` deps — silently never
 * measures when the observed element renders CONDITIONALLY. The compliance
 * panel returns a loading state first, so at mount `ref.current` is null, the
 * effect bails, and with no deps to re-run on the width stays null forever: the
 * panel would then render its wide layout at every width, in production as well
 * as in tests. A callback ref fires when the node actually attaches, which is
 * the event we care about.
 *
 * Returns `null` until the first measurement, so callers can render their
 * preferred default rather than flashing the narrow layout.
 */
export function useElementWidth<T extends HTMLElement>(): {
  ref: (node: T | null) => void;
  width: number | null;
} {
  const [width, setWidth] = useState<number | null>(null);
  const observerRef = useRef<globalThis.ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    // Detach any previous observation first — the node can change.
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;

    // Measure immediately, so there is a width on the first paint after the
    // element appears. This is also what makes the hook assertable in jsdom,
    // which has no ResizeObserver.
    const initial = node.getBoundingClientRect().width;
    if (initial > 0) setWidth(initial);

    if (typeof globalThis.ResizeObserver === 'undefined') return;

    const ro = new globalThis.ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        // Ignore the 0 a detached/hidden element reports — a collapsed pane
        // must not permanently pin the panel to its narrow layout.
        if (w > 0) setWidth(w);
      }
    });
    ro.observe(node);
    observerRef.current = ro;
  }, []);

  // Disconnect on unmount.
  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    },
    [],
  );

  return { ref, width };
}
